import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  BookmarkIcon,
  XMarkIcon,
  BuildingOffice2Icon,
  ChevronDownIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { ChiffMenuProps, Ensemble, CategorieEnsemble, getEnsemblePrestationCount } from '../types/EnsembleTypes';
import { ensemblesPredefinis } from '../data/ensemblesData';
import ChiffMenuSearchBar from './ChiffMenuSearchBar';

// Données d'exemple pour les ensembles prédéfinis
const categoriesEnsembles: CategorieEnsemble[] = [
  {
    id: 'menuiserie',
    nom: 'Menuiserie',
    description: 'Portes, fenêtres, volets',
    couleur: 'bg-amber-100 text-amber-800',
    icon: '🚪'
  },
  {
    id: 'electricite',
    nom: 'Électricité',
    description: 'Installations électriques',
    couleur: 'bg-yellow-100 text-yellow-800',
    icon: '⚡'
  },
  {
    id: 'plomberie',
    nom: 'Plomberie',
    description: 'Installations sanitaires',
    couleur: 'bg-blue-100 text-blue-800',
    icon: '🚿'
  },
  {
    id: 'amenagement',
    nom: 'Aménagement',
    description: 'Cloisons, mobilier, finitions',
    couleur: 'bg-green-100 text-green-800',
    icon: '🏠'
  },
  {
    id: 'gros-oeuvre',
    nom: 'Gros œuvre',
    description: 'Fondations, dalles, murs porteurs',
    couleur: 'bg-stone-100 text-stone-800',
    icon: '🧱'
  }
];

const ChiffMenu: React.FC<ChiffMenuProps> = ({
  onSearch,
  onAddEnsemble,
  onSaveSelectionAsEnsemble,
  onSearchResultSelect,
  selectedBlocks,
  allBlocks,
  userEnsembles,
  onModalOpenChange,
  onClearSelection,
  onConsulter,
}) => {
  const [showEnsembleDropdown, setShowEnsembleDropdown] = useState(false);
  const [showSaveEnsembleModal, setShowSaveEnsembleModal] = useState(false);
  const [newEnsembleNom, setNewEnsembleNom] = useState('');
  const [newEnsembleDescription, setNewEnsembleDescription] = useState('');
  const [ensembleSearchQuery, setEnsembleSearchQuery] = useState('');
  // Multi-select des catégories : Set vide = "Toutes" (aucun filtre).
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const categoriesMenuRef = useRef<HTMLDivElement>(null);

  // Fermeture au clic extérieur du multi-select catégories.
  useEffect(() => {
    if (!showCategoriesMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (categoriesMenuRef.current && !categoriesMenuRef.current.contains(e.target as Node)) {
        setShowCategoriesMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoriesMenu]);

  const toggleCategorie = (id: string) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Le bouton Ensembles est polymorphe : sauvegarde quand ≥2 lignes sont
  // sélectionnées (contexte : l'utilisateur veut capitaliser sa sélection),
  // sinon dropdown de recherche/insertion (contexte : l'utilisateur cherche
  // un ensemble existant). Un seul bouton, deux rôles contextuels.
  const isSaveMode = selectedBlocks.length >= 2;

  // Filtrage des ensembles — prédéfinis + créés à la volée par l'utilisateur.
  // Les userEnsembles apparaissent en tête (plus récents d'abord, voir push
  // côté container). Les deux modes ('groupe' et 'ventilation') sont inclus :
  // le handler handleAddEnsemble côté container traite déjà les deux.
  const filteredEnsembles = useMemo<Ensemble[]>(() => {
    const allEnsembles: Ensemble[] = [...(userEnsembles ?? []), ...ensemblesPredefinis];
    return allEnsembles.filter((ensemble) => {
      const matchesSearch = ensembleSearchQuery === '' ||
        ensemble.nom.toLowerCase().includes(ensembleSearchQuery.toLowerCase()) ||
        ensemble.description.toLowerCase().includes(ensembleSearchQuery.toLowerCase()) ||
        ensemble.tags.some(tag => tag.toLowerCase().includes(ensembleSearchQuery.toLowerCase()));

      const matchesCategorie = selectedCategories.size === 0 || selectedCategories.has(ensemble.categorie);

      return matchesSearch && matchesCategorie;
    });
  }, [userEnsembles, ensembleSearchQuery, selectedCategories]);

  // Classement des catégories par nombre d'ensembles (toutes sources, non filtré
  // par recherche → ordre stable). Une seule pill inline (top 1) pour tenir sur
  // une ligne ; le reste est replié dans un dropdown "+ N autres".
  const VISIBLE_PILLS_LIMIT = 1;
  const categoriesRanked = useMemo(() => {
    const allEnsembles: Ensemble[] = [...(userEnsembles ?? []), ...ensemblesPredefinis];
    const counts = new Map<string, number>();
    for (const e of allEnsembles) {
      counts.set(e.categorie, (counts.get(e.categorie) ?? 0) + 1);
    }
    return categoriesEnsembles
      .map(cat => ({ ...cat, count: counts.get(cat.id) ?? 0 }))
      .sort((a, b) => b.count - a.count);
  }, [userEnsembles]);

  const visibleCategories = categoriesRanked.slice(0, VISIBLE_PILLS_LIMIT);
  const overflowCategories = categoriesRanked.slice(VISIBLE_PILLS_LIMIT);

  const handleEnsembleSelect = (ensemble: Ensemble) => {
    // Appeler directement onAddEnsemble avec l'ensemble original
    // La conversion sera faite dans Chiff_main.tsx
    onAddEnsemble(ensemble);
    setShowEnsembleDropdown(false);
  };

  const handleSaveSelectionAsEnsemble = () => {
    if (selectedBlocks.length < 2) {
      alert('Veuillez sélectionner au moins 2 prestations pour créer un ensemble');
      return;
    }
    
    // Notifier immédiatement l'ouverture de la modal pour empêcher la désélection
    onModalOpenChange(true);
    setShowSaveEnsembleModal(true);
  };

  const confirmSaveEnsemble = () => {
    if (newEnsembleNom.trim() === '') {
      alert('Veuillez saisir un nom pour l\'ensemble');
      return;
    }
    
    onSaveSelectionAsEnsemble(selectedBlocks, newEnsembleNom, newEnsembleDescription);
    setShowSaveEnsembleModal(false);
    onModalOpenChange(false);
    setNewEnsembleNom('');
    setNewEnsembleDescription('');
  };

  const getCategorieInfo = (categorieId: string) => {
    return categoriesEnsembles.find(cat => cat.id === categorieId) || categoriesEnsembles[0];
  };

  return (
    <div className="bg-white px-6 py-4 w-full">
      <div className="flex items-center gap-4">
        {/* Barre de recherche */}
        <div className="flex-1">
          <ChiffMenuSearchBar
            onResultSelect={onSearchResultSelect}
            userEnsembles={userEnsembles}
            className="w-full"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-3">
          {/* Bouton Ensembles — polymorphe :
              · ≥2 lignes sélectionnées → ouvre la modal d'enregistrement
              · sinon → ouvre le dropdown de recherche / insertion */}
          <div className="relative">
            <button
              onClick={() => {
                if (isSaveMode) {
                  handleSaveSelectionAsEnsemble();
                } else {
                  setShowEnsembleDropdown(v => !v);
                }
              }}
              className={`relative group/ens flex items-center space-x-1.5 px-3 py-2 text-sm font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 transition-colors ${
                isSaveMode
                  ? 'text-white bg-violet-600 hover:bg-violet-700 border border-violet-600'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:border-violet-500'
              }`}
            >
              {isSaveMode ? (
                <>
                  <PlusIcon className="h-4 w-4" />
                  <span>Enregistrer ({selectedBlocks.length})</span>
                </>
              ) : (
                <>
                  <BookmarkIcon className="h-4 w-4" />
                  <span>Ensembles</span>
                </>
              )}
              <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 rounded-md bg-gray-900 text-white text-xs whitespace-nowrap opacity-0 group-hover/ens:opacity-100 transition-opacity duration-150 z-50 shadow-lg">
                {isSaveMode
                  ? `Enregistrer la sélection (${selectedBlocks.length} prestations) comme ensemble réutilisable`
                  : 'Retrouver et insérer un ensemble de prestations'}
              </span>
            </button>

            {showEnsembleDropdown && (
              <div className="absolute right-0 mt-2 w-[520px] max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Ensembles prédéfinis</h3>
                    <button
                      onClick={() => setShowEnsembleDropdown(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Barre de recherche des ensembles */}
                  <div className="mb-4">
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={ensembleSearchQuery}
                        onChange={(e) => setEnsembleSearchQuery(e.target.value)}
                        placeholder="Rechercher un ensemble..."
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Filtre catégories — 1 pill inline (top 1) + overflow dropdown */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 flex-nowrap">
                      {visibleCategories.map((categorie) => {
                        const active = selectedCategories.has(categorie.id);
                        return (
                          <button
                            key={categorie.id}
                            type="button"
                            onClick={() => toggleCategorie(categorie.id)}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm transition-colors ${
                              active
                                ? 'bg-violet-600 text-white border border-violet-600'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <span>{categorie.icon}</span>
                            <span>{categorie.nom}</span>
                            <span className={active ? 'text-violet-200' : 'text-gray-400'}>
                              ({categorie.count})
                            </span>
                          </button>
                        );
                      })}

                      {overflowCategories.length > 0 && (
                        <div className="relative" ref={categoriesMenuRef}>
                          <button
                            type="button"
                            onClick={() => setShowCategoriesMenu(v => !v)}
                            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm border border-dashed transition-colors ${
                              overflowCategories.some(c => selectedCategories.has(c.id))
                                ? 'border-violet-400 text-violet-700 bg-violet-50'
                                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                            }`}
                          >
                            <span>+ {overflowCategories.length} autres</span>
                            <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${showCategoriesMenu ? 'rotate-180' : ''}`} />
                          </button>

                          {showCategoriesMenu && (
                            <div className="absolute left-0 top-full mt-1 min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 max-h-64 overflow-y-auto">
                              {overflowCategories.map((categorie) => {
                                const checked = selectedCategories.has(categorie.id);
                                return (
                                  <button
                                    key={categorie.id}
                                    type="button"
                                    onClick={() => toggleCategorie(categorie.id)}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50"
                                  >
                                    <span className={`flex items-center justify-center h-4 w-4 rounded border flex-shrink-0 ${
                                      checked
                                        ? 'bg-violet-600 border-violet-600'
                                        : 'bg-white border-gray-300'
                                    }`}>
                                      {checked && <CheckIcon className="h-3 w-3 text-white" />}
                                    </span>
                                    <span className="flex-1 text-left">
                                      {categorie.icon} {categorie.nom}
                                    </span>
                                    <span className="text-xs text-gray-400 flex-shrink-0">
                                      ({categorie.count})
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedCategories.size > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedCategories(new Set())}
                          className="text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                          Effacer
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Liste des ensembles filtrés */}
                  <div className="space-y-1.5 max-h-[28rem] overflow-y-auto">
                    {filteredEnsembles.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <p>Aucun ensemble trouvé</p>
                      </div>
                    ) : (
                      filteredEnsembles.map((ensemble) => {
                        const categorie = getCategorieInfo(ensemble.categorie);
                        const totalPrestations = getEnsemblePrestationCount(ensemble);
                        const isVentile = ensemble.mode === 'ventilation';
                        return (
                          <div
                            key={ensemble.id}
                            onClick={() => handleEnsembleSelect(ensemble)}
                            className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                            title={`${ensemble.auteur} — ${ensemble.dateCreation.toLocaleDateString()}`}
                          >
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-medium text-gray-900 truncate">{ensemble.nom}</h4>
                              <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                isVentile
                                  ? 'bg-violet-100 text-violet-800'
                                  : 'bg-purple-100 text-purple-800'
                              }`}>
                                {isVentile ? 'Ventilé' : 'Groupe'}
                              </span>
                              <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs ${categorie.couleur}`}>
                                {categorie.icon} {categorie.nom}
                              </span>
                              <span className="flex-shrink-0 text-xs text-gray-400 ml-auto tabular-nums">
                                {totalPrestations} prest.
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5 truncate">{ensemble.description}</p>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bouton Consulter */}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onConsulter?.(selectedBlocks); }}
            disabled={selectedBlocks.length === 0}
            className={`flex items-center space-x-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
              selectedBlocks.length > 0
                ? 'border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100'
                : 'border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
            }`}
            title="Envoyer une demande de devis à des entreprises"
          >
            <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0" />
            <span>Consulter{selectedBlocks.length > 0 ? ` (${selectedBlocks.length})` : ''}</span>
          </button>
        </div>
      </div>


      {/* Modal de sauvegarde d'ensemble */}
      {showSaveEnsembleModal && (
        <div 
          data-modal="true"
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            // Empêcher la fermeture de la modal en cliquant sur l'arrière-plan
            e.stopPropagation();
          }}
        >
          <div 
            className="bg-white rounded-xl p-6 w-full max-w-md mx-4"
            onClick={(e) => {
              // Empêcher la propagation des clics dans la modal
              e.stopPropagation();
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Créer un ensemble</h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSaveEnsembleModal(false);
                  onModalOpenChange(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de l'ensemble
                </label>
                <input
                  type="text"
                  value={newEnsembleNom}
                  onChange={(e) => setNewEnsembleNom(e.target.value)}
                  placeholder="Ex: Bloc-Porte Premium"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={newEnsembleDescription}
                  onChange={(e) => setNewEnsembleDescription(e.target.value)}
                  placeholder="Description de l'ensemble..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>{selectedBlocks.length}</strong> prestation{selectedBlocks.length > 1 ? 's' : ''} sélectionnée{selectedBlocks.length > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowSaveEnsembleModal(false);
                  onModalOpenChange(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  confirmSaveEnsemble();
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700"
              >
                Créer l'ensemble
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChiffMenu;
